import "twin.macro";

import { useMemo } from "react";

import { Divider } from "@mui/material";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/styles";

import { DemandPlanVariant } from "@models/DemandPlanVariant";
import { formatMoneyString, formatNumber } from "@utility/utilityFunctions";
import { pluralize } from "@utils/string";

export const WideTooltip = ({
  width = "none",
  ...props
}: TooltipProps & { width?: number | "none" }) => {
  return useMemo(() => {
    const Comp = styled(({ className, ...props }: TooltipProps) => (
      <Tooltip {...props} classes={{ popper: className }} />
    ))({
      [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: width,
      },
    });
    return <Comp {...props} />;
  }, [width, props]);
};

export const ColumnTooltip = ({ children, title, ...props }) => {
  const { underline, ...tooltipProps } = props;
  return (
    <Tooltip
      enterDelay={500}
      enterNextDelay={500}
      className={underline !== false ? "hover:underline" : ""}
      tw="cursor-default"
      title={title}
      {...tooltipProps}
    >
      {children}
    </Tooltip>
  );
};

export const OrderWindowTooltipTitle = ({
  demandPlanVariant: dpv,
}: {
  demandPlanVariant: DemandPlanVariant;
}) => {
  return (
    <div tw="text-sm">
      <div tw="font-bold text-sm">
        {pluralize("Order Window", dpv.orderWindows, false)}:
      </div>
      <ul tw="list-disc list-inside">
        {dpv.orderWindows.map((ow) => (
          <li key={ow.id}>{ow.name}</li>
        ))}
      </ul>
    </div>
  );
};

export const TotalDemandTooltipTitle = ({
  demandPlanVariant: dpv,
}: {
  demandPlanVariant: DemandPlanVariant;
}) => {
  return (
    <div tw="text-sm">
      <div tw="flex justify-end">
        <span tw="flex-1">Pre-Order:</span>
        <span>{formatNumber(dpv.preorderQuantity)}</span>
      </div>
      <div tw="flex justify-end">
        <span tw="flex-1">Lead Time Inventory:</span>
        <span>&nbsp;+ {formatNumber(dpv.leadTimeInventory)}</span>
      </div>
      <div tw="flex justify-end">
        <span tw="flex-1">Lead Time Safety Stock:</span>
        <span>&nbsp;+ {formatNumber(dpv.leadTimeSafetyStock)}</span>
      </div>
      <div tw="flex justify-end">
        <span tw="flex-1">Demand Coverage Inventory:</span>
        <span>&nbsp;+ {formatNumber(dpv.demandCoverageInventory)}</span>
      </div>
      <div tw="flex justify-end">
        <span tw="flex-1">Demand Coverage Safety Stock:</span>
        <span>&nbsp;+ {formatNumber(dpv.demandCoverageSafetyStock)}</span>
      </div>
      <Divider tw="border-white my-1" />
      <div tw="flex justify-end">
        <span tw="flex-1">Total Demand:</span>
        <span tw="font-bold">&nbsp;= {formatNumber(dpv.totalDemand)}</span>
      </div>
    </div>
  );
};

export const OrderTotalTooltipTitle = ({
  demandPlanVariant: dpv,
}: {
  demandPlanVariant: DemandPlanVariant;
}) => {
  const overrideRecommended =
    dpv.toOrder > 0 && dpv.toOrder !== dpv.recommendedToOrder;
  const orderQuantity = overrideRecommended
    ? dpv.toOrder
    : dpv.recommendedToOrder;
  return (
    <div tw="text-sm">
      <div tw="flex justify-end">
        <span tw="flex-1">Total Demand:</span>
        <span> {formatNumber(dpv.totalDemand)}</span>
      </div>
      <div tw="flex justify-end">
        <span tw="flex-1">On-Hand:</span>
        <span>- {formatNumber(dpv.variant.globalAvailableToOrderQty!)}</span>
      </div>
      <Divider tw="border-white my-1" />
      <div tw="flex justify-end">
        <span tw="flex-1">
          To Be Ordered{" "}
          <span tw="text-xs">(Pack Size {dpv.variant.item.packSize})</span>:
        </span>
        <span tw="ml-4 font-bold">= {formatNumber(orderQuantity)}</span>
      </div>
      {overrideRecommended && (
        <div tw="italic text-xs">
          *Overriding Recommended Order Quantity of{" "}
          {formatNumber(dpv.recommendedToOrder)}
        </div>
      )}
      <div tw="flex justify-end pt-4">
        <span tw="flex-1">
          Total Cost{" "}
          <span tw="text-xs">
            ({formatNumber(orderQuantity)} @{" "}
            {formatMoneyString(dpv.variant.item.cost)})
          </span>
          :
        </span>
        <span tw="ml-4 font-bold">
          {formatMoneyString(orderQuantity * dpv.variant.item.cost)}
        </span>
      </div>
    </div>
  );
};
